import { Theme, ThemeOptions } from '@mui/material';
import { designTokens } from './themes/design-tokens';

export interface ThemeConfig {
  theme: Theme;
  ownerState?: Record<string, unknown>;
}

export const baseThemeConfig: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 1920,
      gs: 2600,
    },
  },
  typography: {
    fontFamily: designTokens.typography.fontFamily.primary,
    h1: {
      fontSize: designTokens.typography.fontSizes.h1,
      fontWeight: designTokens.typography.fontWeights.bold,
      lineHeight: designTokens.typography.lineHeights.h1,
      letterSpacing: designTokens.typography.letterSpacing.h1,
      fontStyle: 'normal',
      [`@media (max-width: 599px)`]: {
        fontSize: designTokens.typography.fontSizes.mobile.h1,
        letterSpacing: designTokens.typography.letterSpacing.mobile.h1,
      },
    },
    h2: {
      fontSize: designTokens.typography.fontSizes.h2,
      fontWeight: designTokens.typography.fontWeights.bold,
      lineHeight: designTokens.typography.lineHeights.h2,
      letterSpacing: designTokens.typography.letterSpacing.h2,
      fontStyle: 'normal',
      [`@media (max-width: 599px)`]: {
        fontSize: designTokens.typography.fontSizes.mobile.h2,
        letterSpacing: designTokens.typography.letterSpacing.mobile.h2,
      },
    },
    h3: {
      fontSize: designTokens.typography.fontSizes.h3,
      fontWeight: designTokens.typography.fontWeights.bold,
      lineHeight: designTokens.typography.lineHeights.h3,
      letterSpacing: designTokens.typography.letterSpacing.h3,
      fontStyle: 'normal',
      [`@media (max-width: 599px)`]: {
        fontSize: designTokens.typography.fontSizes.mobile.h3,
        letterSpacing: designTokens.typography.letterSpacing.mobile.h3,
      },
    },
    h4: {
      fontSize: designTokens.typography.fontSizes.h4,
      fontWeight: designTokens.typography.fontWeights.bold,
      lineHeight: designTokens.typography.lineHeights.h4,
      letterSpacing: designTokens.typography.letterSpacing.h4,
      fontStyle: 'normal',
      [`@media (max-width: 599px)`]: {
        fontSize: designTokens.typography.fontSizes.mobile.h4,
        letterSpacing: designTokens.typography.letterSpacing.mobile.h4,
      },
    },
    h5: {
      fontSize: designTokens.typography.fontSizes.h5,
      fontWeight: designTokens.typography.fontWeights.bold,
      lineHeight: designTokens.typography.lineHeights.h5,
      letterSpacing: designTokens.typography.letterSpacing.h5,
      fontStyle: 'normal',
      [`@media (max-width: 599px)`]: {
        fontSize: designTokens.typography.fontSizes.mobile.h5,
        letterSpacing: designTokens.typography.letterSpacing.mobile.h5,
      },
    },
    h6: {
      fontSize: designTokens.typography.fontSizes.h6,
      fontWeight: designTokens.typography.fontWeights.medium,
      lineHeight: designTokens.typography.lineHeights.h6,
      letterSpacing: designTokens.typography.letterSpacing.h6,
      fontStyle: 'normal',
      [`@media (max-width: 599px)`]: {
        fontSize: designTokens.typography.fontSizes.mobile.h6,
        letterSpacing: designTokens.typography.letterSpacing.mobile.h6,
      },
    },
    subtitle1: {
      fontSize: designTokens.typography.fontSizes.subtitle1,
      fontWeight: designTokens.typography.fontWeights.regular,
      lineHeight: designTokens.typography.lineHeights.subtitle1,
      letterSpacing: designTokens.typography.letterSpacing.subtitle1,
      fontStyle: 'normal',
      [`@media (max-width: 599px)`]: {
        fontSize: designTokens.typography.fontSizes.mobile.subtitle1,
        letterSpacing: designTokens.typography.letterSpacing.mobile.subtitle1,
      },
    },
    subtitle2: {
      fontSize: designTokens.typography.fontSizes.subtitle2,
      fontWeight: designTokens.typography.fontWeights.medium,
      lineHeight: designTokens.typography.lineHeights.subtitle2,
      letterSpacing: designTokens.typography.letterSpacing.subtitle2,
      fontStyle: 'normal',
      [`@media (max-width: 599px)`]: {
        fontSize: designTokens.typography.fontSizes.mobile.subtitle2,
        letterSpacing: designTokens.typography.letterSpacing.mobile.subtitle2,
      },
    },
    body1: {
      fontSize: designTokens.typography.fontSizes.body1,
      fontWeight: designTokens.typography.fontWeights.regular,
      lineHeight: designTokens.typography.lineHeights.body1,
      letterSpacing: designTokens.typography.letterSpacing.body1,
      fontStyle: 'normal',
      [`@media (max-width: 599px)`]: {
        fontSize: designTokens.typography.fontSizes.mobile.body1,
        letterSpacing: designTokens.typography.letterSpacing.mobile.body1,
      },
    },
    body2: {
      fontSize: designTokens.typography.fontSizes.body2,
      fontWeight: designTokens.typography.fontWeights.regular,
      lineHeight: designTokens.typography.lineHeights.body2,
      letterSpacing: designTokens.typography.letterSpacing.body2,
      fontStyle: 'normal',
      [`@media (max-width: 599px)`]: {
        fontSize: designTokens.typography.fontSizes.mobile.body2,
        letterSpacing: designTokens.typography.letterSpacing.mobile.body2,
      },
    },
    caption: {
      fontSize: designTokens.typography.fontSizes.caption,
      fontWeight: designTokens.typography.fontWeights.medium,
      lineHeight: designTokens.typography.lineHeights.caption,
      letterSpacing: designTokens.typography.letterSpacing.caption,
      fontStyle: 'normal',
      [`@media (max-width: 599px)`]: {
        fontSize: designTokens.typography.fontSizes.mobile.caption,
        letterSpacing: designTokens.typography.letterSpacing.mobile.caption,
      },
    },
    button: {
      fontWeight: designTokens.typography.fontWeights.medium,
      lineHeight: designTokens.typography.lineHeights.body2,
      letterSpacing: designTokens.typography.letterSpacing.body2,
      fontStyle: 'normal',
      textTransform: 'none' as const,
    },
  },
  customShape: {
    borderRadiusXSmall: designTokens.borderRadius.xs,
    borderRadiusSmall: designTokens.borderRadius.sm,
    borderRadiusMedium: designTokens.borderRadius.md,
    borderRadiusLarge: designTokens.borderRadius.lg,
    borderRadiusXLarge: designTokens.borderRadius.xl,
    borderRadius2XL: designTokens.borderRadius['2xl'],
    borderRadius3XL: designTokens.borderRadius['3xl'],
    borderRadius4XL: designTokens.borderRadius['4xl'],
    borderRadius5XL: designTokens.borderRadius['5xl'],
    borderRadiusFull: designTokens.borderRadius.full,
  },
  spacing: designTokens.spacing.xs,
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: ({ theme }: ThemeConfig) => ({
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }: ThemeConfig) => ({
          borderRadius: theme.spacing(3),
          boxShadow: theme.shadows[2],
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }: ThemeConfig) => ({
          borderRadius: theme.shape.borderRadius,
          backgroundImage: 'none',
        }),
        elevation1: ({ theme }: ThemeConfig) => ({
          boxShadow: theme.shadows[1],
        }),
        elevation2: ({ theme }: ThemeConfig) => ({
          boxShadow: theme.shadows[2],
        }),
        elevation3: ({ theme }: ThemeConfig) => ({
          boxShadow: theme.shadows[3],
        }),
      },
    },
  },
};
